import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import { ConfigKey, getEnvVariable } from '@legacy-modules/common/services/EnvironmentConfigurationService';
import { selectTokenId } from '@redux';

export type UpdateUserConfigFn<T> = (setter: Partial<T>) => void;
type InputParameters<T> = {
  configKey: string;
  defaultState?: Partial<T>;
};
type UseUserConfigOutput<T> = { data: Partial<T>; isLoading: boolean; mutate: UpdateUserConfigFn<T> };

const PATH = 'user-configs';

const useUserConfig = <T = unknown,>(parameters: InputParameters<T>): UseUserConfigOutput<T> => {
  const { configKey, defaultState } = parameters;
  const queryClient = useQueryClient();
  const tokenId = useSelector(selectTokenId);

  const url = new URL(`${getEnvVariable(ConfigKey.HTTP_API_URL)}/${PATH}`);
  url.searchParams.append('configKey', configKey);

  const { data, isLoading } = useQuery<T>({
    queryKey: [PATH, configKey],
    queryFn: () =>
      fetch(url, {
        headers: {
          token: tokenId,
        },
      }).then((res) => res.json()),
    enabled: !!configKey,
  });

  const { mutate } = useMutation<unknown, unknown, T>({
    mutationFn: (state: T) =>
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: tokenId,
        },
        body: JSON.stringify(state),
      }),
    onMutate: (state: T) => {
      queryClient.cancelQueries({ queryKey: [PATH, configKey] });
      const previous = queryClient.getQueryData([PATH, configKey]);
      queryClient.setQueryData([PATH, configKey], state);
      return previous;
    },
    onError: (error, state, previous) => {
      queryClient.setQueryData([PATH, configKey], previous);
    },
    onSettled: async () => await queryClient.invalidateQueries({ queryKey: [PATH, configKey] }),
  });

  return { data: data ?? defaultState, mutate, isLoading };
};

export default useUserConfig;
